import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ELSI_API_URL, errorCallback, makeUrl, succesCallbackEmpty } from '../../const/const';
import { getFetchRequest, MAKE_USER_REQUEST, GET_CURRENT_USER, postFetchRequest, PASSWORD_RESET_SUBMIT, PASSWORD_RESET_INFO, PAGE_CSRF_URL, AUTH_LOGIN_URL } from '../../store/requests';
import { userService } from '../../services/userService';
import CustomSelect from '../CustomSelect';

const getCurrentUser = () => {
    const result = `${ELSI_API_URL}/api/v1/user/current/`;
    return result;
};

const TabProfile = ({ resetCode }) => {
    const [userId, setUserId] = useState('');
    const [profileFirstName, setProfileFirstName] = useState('');
    const [profileLastName, setProfileLastName] = useState('');
    const [profileMiddleName, setProfileMiddleName] = useState('');
    const [profileFullName, setProfileFullName] = useState('');
    const [profilePhone, setProfilePhone] = useState('');
    const [profileEmail, setProfileEmail] = useState('');
    const [priceCurrency, setPriceCurrency] = useState('RUB');
    const [currencySaving, setCurrencySaving] = useState(false);
    const [currencyMessage, setCurrencyMessage] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');
    const [password, setPassword] = useState('');
    const [repeatPassword, setRepeatPassword] = useState('');
    const [error, setError] = useState({type:'', message:''});
    const [customerRole, setCustomerRole] = useState('');
    const [agreeToTerms, setAgreeToTerms] = useState(false);
    const [hasPasswordBeenSaved, setHasPasswordBeenSaved] = useState(false);
    const [isEditingPassword, setIsEditingPassword] = useState(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showRepeatPassword, setShowRepeatPassword] = useState(false);
    const [isVerifyingCurrent, setIsVerifyingCurrent] = useState(false);
    const [isCurrentPasswordValid, setIsCurrentPasswordValid] = useState(null);
    const currentPwdVerifyTimerRef = useRef(null);
    const currentPwdControllerRef = useRef(null);

    const navigate = useNavigate();

    const getPasswordError = (pwd) => {
        if (!pwd) return null;
        const hasLowercaseLetter = /[a-z]/.test(pwd);
        const hasUppercaseLetter = /[A-Z]/.test(pwd);
        if (!hasLowercaseLetter && !hasUppercaseLetter) return 'Только латинские символы';
        if (pwd.length < 8) return 'Слишком короткий пароль';
        if (pwd.length > 20) return 'Слишком длинный пароль';
        if (!hasLowercaseLetter) return 'Нет строчной буквы';
        if (!hasUppercaseLetter) return 'Нет заглавной буквы';
        return null;
    };

    const succesCallback = (response) => {
        // Обрабатываем только валидный объект пользователя
        if (!response || !response.id) {
            return;
        }

        setUserId(response.id);

        const nameParts = response.name ? response.name.split(' ') : ['', '', ''];
        setProfileLastName(nameParts[0] || '');
        setProfileFirstName(nameParts[1] || '');
        setProfileMiddleName(nameParts[2] || '');
        setProfilePhone(response.phone);
        setProfileEmail(response.email);
        setProfileFullName(response.name || '');
        setPriceCurrency(response.priceCurrency === 'USD' ? 'USD' : 'RUB');
        try {
            localStorage.setItem('elsi_price_currency', response.priceCurrency === 'USD' ? 'USD' : 'RUB');
        } catch (e) {
            console.warn('Failed to store price currency', e);
        }

        // Пользователь авторизован: пароль ранее установлен
        setHasPasswordBeenSaved(true);
        setIsEditingPassword(false);
        setCurrentPassword('');
    };

    useEffect(()=> {
        // В режиме первичной установки (есть resetCode) не дергаем текущего пользователя без токена
        if (!resetCode) {
            getFetchRequest(getCurrentUser(), succesCallback, errorCallback);
        }
    }, [resetCode]);

    // Подгружаем данные пользователя по коду (если пришли через письмо), чтобы заполнить форму
    useEffect(() => {
        if (resetCode && !userId) {
            fetch(makeUrl([PASSWORD_RESET_INFO]), {
                credentials: 'include',
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code: resetCode }),
            }).then(async res => {
                if (res.ok) {
                    const data = await res.json();
                    if (data) {
                        setUserId(data.id || '');
                        setProfileEmail(data.email || '');
                        setProfilePhone(data.phone || '');
                        setProfileFullName(data.name || '');
                    }
                }
            }).catch(() => {});
        }
    }, [resetCode, userId]);

    // Если есть resetCode в URL, показываем режим первичной установки пароля (без текущего пароля)
    useEffect(() => {
        if (resetCode) {
            setHasPasswordBeenSaved(false);
            setIsEditingPassword(true);
        }
    }, [resetCode]);

    const handleCurrencyChange = (event) => {
        const nextCurrency = event.target.value === 'USD' ? 'USD' : 'RUB';
        setPriceCurrency(nextCurrency);
        setCurrencyMessage('');
        if (!userId) return;
        setCurrencySaving(true);
        postFetchRequest(
            makeUrl([MAKE_USER_REQUEST, userId, '/currency/']),
            { priceCurrency: nextCurrency },
            (res) => {
                setCurrencySaving(false);
                if (res && res.priceCurrency) {
                    try {
                        localStorage.setItem('elsi_price_currency', res.priceCurrency === 'USD' ? 'USD' : 'RUB');
                    } catch (e) {
                        console.warn('Failed to store price currency', e);
                    }
                    setCurrencyMessage('Валюта сохранена');
                } else {
                    setCurrencyMessage('Не удалось сохранить валюту');
                }
                setTimeout(() => setCurrencyMessage(''), 2500);
            },
            () => {
                setCurrencySaving(false);
                setCurrencyMessage('Не удалось сохранить валюту');
            },
        );
    };

    const handleCheckPasswords = (e) => {
        const confirmValue = e.target.value;
        setRepeatPassword(confirmValue);
        if(password) {
            const pwdError = getPasswordError(password);
            if(pwdError) {
                setError({type: 'password', message: pwdError});
                return;
            }
            if(confirmValue === password) {
                setError({type:'', message:''});
            } else {
                setError({type: 'password', message: 'Пароли не совпадают'});
            }
        } else {
            setError({type:'', message:''});
        }
    };

    const handlePasswordChange = (e) => {
        const newPassword = e.target.value;
        setPassword(newPassword);
        // Проверка сложности пароля
        const pwdError = getPasswordError(newPassword);
        if (newPassword && pwdError) {
            setError({type: 'password', message: pwdError});
            return;
        }
        // Если пароль изменился и у нас есть поле повторного пароля, обновляем валидацию совпадения
        if ((!hasPasswordBeenSaved || isEditingPassword) && repeatPassword) {
            if(repeatPassword === newPassword) {
                setError({type:'', message:''});
            } else {
                setError({type: 'password', message: 'Пароли не совпадают'});
            }
        } else {
            setError({type:'', message:''});
        }
        // Не нужно обновлять localStorage при редактировании, это будет сделано только при сохранении
    };

    const verifyCurrentPassword = (value) => {
        if (!value || value.length < 8) {
            setIsCurrentPasswordValid(null);
            setError(prev => prev.type === 'current_password' ? { type: '', message: '' } : prev);
            return;
        }
        try {
            if (currentPwdControllerRef.current) {
                currentPwdControllerRef.current.abort();
            }
            const controller = new AbortController();
            currentPwdControllerRef.current = controller;
            setIsVerifyingCurrent(true);
            fetch(makeUrl([AUTH_LOGIN_URL]), {
                credentials: 'omit',
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                signal: controller.signal,
                body: JSON.stringify({ login: profileEmail, password: value }),
            }).then(async res => {
                if (res.ok) {
                    setIsCurrentPasswordValid(true);
                    setError(prev => prev.type === 'current_password' ? { type: '', message: '' } : prev);
                } else {
                    setIsCurrentPasswordValid(false);
                    setError({ type: 'current_password', message: 'Пароль не верный' });
                }
            }).catch(err => {
                if (err.name !== 'AbortError') {
                    setIsCurrentPasswordValid(null);
                }
            }).finally(() => setIsVerifyingCurrent(false));
        } catch (_) {
            setIsVerifyingCurrent(false);
        }
    };

    const handleCurrentPasswordChange = (e) => {
        const value = e.target.value;
        setCurrentPassword(value);
        // debounce проверка
        if (currentPwdVerifyTimerRef.current) {
            clearTimeout(currentPwdVerifyTimerRef.current);
        }
        currentPwdVerifyTimerRef.current = setTimeout(() => verifyCurrentPassword(value), 300);
    };

    const handleEditPassword = () => {
        setIsEditingPassword(true);
        setPassword('');
        setRepeatPassword('');
        setError({type:'', message:''});
        // Сбрасываем состояние проверки текущего пароля
        setCurrentPassword('');
        setIsCurrentPasswordValid(null);
        setIsVerifyingCurrent(false);
        if (currentPwdVerifyTimerRef.current) {
            clearTimeout(currentPwdVerifyTimerRef.current);
            currentPwdVerifyTimerRef.current = null;
        }
        if (currentPwdControllerRef.current) {
            try { currentPwdControllerRef.current.abort(); } catch(_) {/* noop: controller may already be aborted */ }
            currentPwdControllerRef.current = null;
        }
    };

    const toggleCurrentPasswordVisibility = () => {
        setShowCurrentPassword(!showCurrentPassword);
    };

    const successUpdateData = () => {
        console.log('Данные успешно сохранены!');
        
        // Если пароль был введен, обновляем статусы (без сохранения паролей)
        if (password) {
            setHasPasswordBeenSaved(true);
            setIsEditingPassword(false);
            setCurrentPassword('');
            setIsCurrentPasswordValid(null);
            setIsVerifyingCurrent(false);
            setShowCurrentPassword(false);
            setShowPassword(false);
            setShowRepeatPassword(false);
            if (currentPwdVerifyTimerRef.current) {
                clearTimeout(currentPwdVerifyTimerRef.current);
                currentPwdVerifyTimerRef.current = null;
            }
            if (currentPwdControllerRef.current) {
                try { currentPwdControllerRef.current.abort(); } catch(_) {/* noop: controller may already be aborted */ }
                currentPwdControllerRef.current = null;
            }
        }
        
        // Очищаем поля нового пароля
        setPassword('');
        setRepeatPassword('');
        
        // Показываем успешное сообщение
        setError({type: 'success', message: 'Данные профиля успешно сохранены!'});
        
        // Убираем сообщение через 3 секунды
        setTimeout(() => {
            setError({type: '', message: ''});
        }, 3000);
    };

    const handleSubmitForm = (e) => {
        e.preventDefault();
        
        // Проверяем валидацию паролей
        const passwordErrorMsg = getPasswordError(password);
        const isPasswordValid = (!hasPasswordBeenSaved || isEditingPassword) 
            ? (!password || (!passwordErrorMsg && password === repeatPassword))
            : true;
        
        if(!userId) {
            setError({type: 'general', message: 'Пользователь еще не загружен, попробуйте снова'});
            return;
        }
        
        if(resetCode && !agreeToTerms) {
            setError({type: 'agreement', message: 'Необходимо принять условия соглашения'});
            return;
        }
        
        if(!isPasswordValid) {
            if (password && passwordErrorMsg) {
                setError({type: 'password', message: passwordErrorMsg});
            } else {
                setError({type: 'password', message: 'Не совпадают'});
            }
            return;
        }

        // Очищаем предыдущие ошибки
        setError({type: '', message: ''});
        
        const fullName = `${profileFullName}`.trim();
        const userData = {
            email: profileEmail,
            phone: profilePhone,
            name: fullName,
            customerRole,
        };
        
        if(password) {
            userData.password = password;
        }
        
        console.log('Сохраняем данные пользователя:', userData);
        const submitProfile = (onSuccess) => postFetchRequest(
            makeUrl([MAKE_USER_REQUEST, userId, '/']),
            userData,
            (res) => {
                if (res && res.ok) {
                    successUpdateData();
                    if (typeof onSuccess === 'function') onSuccess();
                } else {
                    setError({ type: 'password', message: 'Не удалось сохранить изменения' });
                }
            },
            (e) => {
                errorCallback(e);
                setError({ type: 'password', message: 'Не удалось сохранить изменения' });
            },
            true,
        );

        const autoLogin = () => {
            // Выполняем логин по email + только что заданному паролю
            const loginBody = { login: profileEmail, password };
            postFetchRequest(
                makeUrl([AUTH_LOGIN_URL]),
                loginBody,
                (loginResponse) => {
                    const token = loginResponse && loginResponse.token;
                    if (token) {
                        localStorage.setItem('token', token);
                        // Обновляем пользовательскую информацию
                        userService.refreshUser().catch(() => {});
                        // Сохраняем профиль и после успешного сохранения делаем полный редирект
                        submitProfile(() => {
                            setTimeout(() => {
                                window.location.replace('/home');
                            }, 50);
                        });
                    } else {
                        // Пароль сохранился, но логин не прошёл — предложим войти вручную
                        setError({ type: 'password', message: 'Пароль сохранён, но авто-логин не удался. Войдите вручную.' });
                    }
                },
                () => setError({ type: 'password', message: 'Пароль сохранён, но авто-логин не удался. Войдите вручную.' }),
            );
        };

        // Если есть resetCode, сначала сохраним пароль по коду (создаст пароль без логина)
        if (resetCode && password) {
            // 1) Получаем CSRF код
            fetch(makeUrl([PAGE_CSRF_URL]), {
                credentials: 'include',
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            }).then(async csrfRes => {
                if (!csrfRes.ok) {
                    setError({type:'password', message:'Не удалось получить CSRF. Попробуйте обновить страницу.'});
                    return;
                }
                const { csrf } = await csrfRes.json();
                // 2) Отправляем пароль с code и csrf
                return fetch(makeUrl([PASSWORD_RESET_SUBMIT]), {
                    credentials: 'include',
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ code: resetCode, password, csrf }),
                });
            }).then(res => {
                if (!res) return; // уже обработано
                if (!res.ok) {
                    setError({type:'password', message:'Не удалось сохранить пароль. Ссылка могла устареть.'});
                    return;
                }
                // После успешного сохранения пароля — авто-логин, затем обновление профиля и переход на домашнюю
                autoLogin();
            }).catch(() => setError({type:'password', message:'Ошибка сети при сохранении пароля'}));
        } else {
            // Если нет resetCode и редактируем пароль — требуем текущий пароль и проверяем его
            if (isEditingPassword && password) {
                if (!currentPassword) {
                    setError({ type: 'password', message: 'Введите текущий пароль' });
                    return;
                }
                if (isCurrentPasswordValid === false) {
                    setError({ type: 'password', message: 'Текущий пароль неверен' });
                    return;
                }
                // Защитная повторная проверка без изменения сессии и токена
                fetch(makeUrl([AUTH_LOGIN_URL]), {
                    credentials: 'omit',
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ login: profileEmail, password: currentPassword }),
                }).then(res => {
                    if (!res.ok) {
                        setError({ type: 'password', message: 'Текущий пароль неверен' });
                        return;
                    }
                    submitProfile();
                }).catch(() => setError({ type: 'password', message: 'Не удалось проверить текущий пароль' }));
            } else {
                // Нет изменений пароля — ничего не делаем
                return;
            }
        }
    };

    // Вспомогательные флаги для UI-индикации и доступности кнопки Сохранить
    const isCurrentGreen = (!resetCode) && isEditingPassword && (currentPassword || '').length >= 8 && isCurrentPasswordValid === true;
    const isNewRepeatGreen = Boolean(password && repeatPassword && password === repeatPassword && !getPasswordError(password));
    const isSaveDisabled = resetCode ? (!(agreeToTerms && isNewRepeatGreen)) : (!(isCurrentGreen && isNewRepeatGreen));

    return (
        <>
            <div className="setup-customer__section">
                <h3 className="setup-customer__title">Данные профиля</h3>
                <div className="profile-form__two-columns">
                    <div className="profile-form__left-column">
                        <form className="profile-form setup-customer__form">
                            <label name="representative" className="profile-form__item">Представитель организации
                                <input className="profile-form__input" placeholder="−" value={profileFullName || ''} onChange={(e) => setProfileFullName(e.target.value)} disabled/>
                            </label>
                            <label name="email" className="profile-form__item">Электронная почта 
                                <input className="profile-form__input" placeholder="−" value={profileEmail || ''} onChange={(e) => setProfileEmail(e.target.value)} disabled/>
                            </label>
                            <label name="phone" className="profile-form__item">Телефон
                                <input className="profile-form__input" placeholder="−" value={profilePhone || ''} onChange={(e) => setProfilePhone(e.target.value)} disabled/>
                            </label>
                            <label name="login" className="profile-form__item">Логин 
                                <input className="profile-form__input" placeholder="−" value={profileEmail || ''} onChange={(e) => setProfileEmail(e.target.value)} disabled/>
                            </label>
                            {/* <CustomSelect customerRole={customerRole} setCustomerRole={setCustomerRole}/> */}
                        </form>
                    </div>
                    <div className="profile-form__right-column">
                        <form className="profile-form setup-customer__form">
                            {/* Кнопка изменить пароль - показывается если пароль сохранен и не редактируется */}
                            {hasPasswordBeenSaved && !isEditingPassword && (
                                <div className="profile-form__edit-password">
                                    <button 
                                        type="button"
                                        className="profile-form__edit-password-btn setup-customer__add-btn"
                                        onClick={handleEditPassword}
                                    >
                                        Изменить пароль
                                    </button>
                                </div>
                            )}

                            {/* Поля для нового пароля - показываются если пароль не сохранен ИЛИ редактируется */}
                            {(!hasPasswordBeenSaved || isEditingPassword) && (
                                <>
                                    {/* Текущий пароль запрашиваем только для уже сохранённого пароля и в режиме редактирования */}
                                    {(!resetCode) && (
                                        <label name="current-password" className="profile-form__item">
                                            Введите текущий пароль {error.type==='current_password'? <span className='alert' style={{marginLeft: '8px'}}>{error.message}</span> : ''}
                                            <div className="profile-form__password-container">
                                                <input 
                                                    type={showCurrentPassword ? 'text' : 'password'}
                                                    className={`profile-form__input passwords__input ${(isCurrentPasswordValid && (currentPassword || '').length >= 8) ? 'passwords__input_matched' : ''}`} 
                                                    placeholder="−" 
                                                    value={currentPassword || ''} 
                                                    onChange={handleCurrentPasswordChange}
                                                />
                                                <button 
                                                    type="button"
                                                    className="profile-form__password-toggle "
                                                    onClick={toggleCurrentPasswordVisibility}
                                                >
                                                    <svg width="22" height="18" viewBox="0 0 22 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M1 8.59113C1 8.59113 10 -5.00405 21 8.59113" stroke={showCurrentPassword ? '#83838D' : '#1C1C1C'} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                                                        <path d="M21 8.58984C21 8.58984 12 22.185 1 8.58984" stroke={showCurrentPassword ? '#83838D' : '#1C1C1C'} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                                                        <path d="M3.40918 16.1818L18.591 1" stroke={showCurrentPassword ? '#83838D' : '#1C1C1C'} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                                                        <path d="M8.49707 11.0529C9.17075 11.7265 10.0845 12.105 11.0372 12.105C11.9899 12.105 12.9036 11.7265 13.5773 11.0529C14.251 10.3792 14.6294 9.46549 14.6294 8.51276C14.6294 7.56004 14.251 6.64633 13.5773 5.97266L8.49707 11.0529Z" stroke={showCurrentPassword ? '#83838D' : '#1C1C1C'} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                                                    </svg>
                                                </button>
                                            </div>
                                        </label>
                                    )}
                                    <label name="password" className="profile-form__item">
                                        {resetCode ? 'Пароль' : 'Новый пароль'} <span className="profile-form__item-hint">? <span className="profile-form__item-hint-text">Длиной от 8 до 20 знаков и содержит буквы разных регистров.</span></span>
                                        <div className="profile-form__password-container">
                                            <input 
                                                type={showPassword ? 'text' : 'password'}
                                                className={`profile-form__input passwords__input ${isNewRepeatGreen ? 'passwords__input_matched' : ''}`} 
                                                placeholder="−" 
                                                value={password || ''} 
                                                onChange={handlePasswordChange}
                                            />
                                            <button 
                                                type="button"
                                                className="profile-form__password-toggle"
                                                onClick={() => setShowPassword(prev => !prev)}
                                                aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                                            >
                                                <svg width="22" height="18" viewBox="0 0 22 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M1 8.59113C1 8.59113 10 -5.00405 21 8.59113" stroke={showPassword ? '#83838D' : '#1C1C1C'} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M21 8.58984C21 8.58984 12 22.185 1 8.58984" stroke={showPassword ? '#83838D' : '#1C1C1C'} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M3.40918 16.1818L18.591 1" stroke={showPassword ? '#83838D' : '#1C1C1C'} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M8.49707 11.0529C9.17075 11.7265 10.0845 12.105 11.0372 12.105C11.9899 12.105 12.9036 11.7265 13.5773 11.0529C14.251 10.3792 14.6294 9.46549 14.6294 8.51276C14.6294 7.56004 14.251 6.64633 13.5773 5.97266L8.49707 11.0529Z" stroke={showPassword ? '#83838D' : '#1C1C1C'} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            </button>
                                        </div>
                                    </label>

                                    <label name="repeat-password" className="profile-form__item">
                                        Повторить пароль
                                        <div className="profile-form__password-container">
                                            <input 
                                                type={showRepeatPassword ? 'text' : 'password'} 
                                                className={`profile-form__input passwords__input ${isNewRepeatGreen ? 'passwords__input_matched' : ''}`} 
                                                placeholder="−" 
                                                value={repeatPassword || ''} 
                                                onChange={(e) => handleCheckPasswords(e)}
                                            />
                                            <button 
                                                type="button"
                                                className="profile-form__password-toggle"
                                                onClick={() => setShowRepeatPassword(prev => !prev)}
                                                aria-label={showRepeatPassword ? 'Скрыть пароль' : 'Показать пароль'}
                                            >
                                                <svg width="22" height="18" viewBox="0 0 22 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M1 8.59113C1 8.59113 10 -5.00405 21 8.59113" stroke={showRepeatPassword ? '#83838D' : '#1C1C1C'} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M21 8.58984C21 8.58984 12 22.185 1 8.58984" stroke={showRepeatPassword ? '#83838D' : '#1C1C1C'} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M3.40918 16.1818L18.591 1" stroke={showRepeatPassword ? '#83838D' : '#1C1C1C'} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M8.49707 11.0529C9.17075 11.7265 10.0845 12.105 11.0372 12.105C11.9899 12.105 12.9036 11.7265 13.5773 11.0529C14.251 10.3792 14.6294 9.46549 14.6294 8.51276C14.6294 7.56004 14.251 6.64633 13.5773 5.97266L8.49707 11.0529Z" stroke={showRepeatPassword ? '#83838D' : '#1C1C1C'} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            </button>
                                        </div>
                                        {error.type==='password'? <p className='alert'>{error.message}</p>: ''}
                                    </label>
                                </>
                            )}
                        </form>
                    </div>
                </div>
                
                {/* Показываем соглашение и кнопку сохранения только в режимах установки/редактирования пароля */}
                {!(hasPasswordBeenSaved && !isEditingPassword) ? (
                    <>
                        {resetCode && (
                            <div className="profile-form__agreement">
                                <label className="profile-form__agreement-label" htmlFor="agree-to-terms">
                                    <input 
                                        id="agree-to-terms"
                                        type="checkbox" 
                                        checked={agreeToTerms} 
                                        onChange={(e) => setAgreeToTerms(e.target.checked)}
                                        className="visually-hidden"
                                        role="switch"
                                        aria-checked={agreeToTerms}
                                    />
                                    <span 
                                        className="consent-toggle" 
                                        aria-hidden="true"
                                        onClick={() => setAgreeToTerms(prev => !prev)}
                                    ></span>
                                    <span className="profile-form__agreement-checkbox-container">
                                        Я принимаю условия <a href="#" className="profile-form__agreement-link" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>Пользовательского соглашения</a> и <a href="#" className="profile-form__agreement-link" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>Политики конфиденциальности.</a>
                                    </span>
                                </label>
                            </div>
                        )}
                        
                        {/* Сообщение об успехе */}
                        {error.type === 'success' && error.message && (
                            <div className={'alert alert-success'}>
                                {error.message}
                            </div>
                        )}
                        
                        <div className="profile-form__submit-section">
                            {(hasPasswordBeenSaved && isEditingPassword) && (
                                <button 
                                    onClick={() => {
                                        setIsEditingPassword(false);
                                        setCurrentPassword('');
                                        setPassword('');
                                        setRepeatPassword('');
                                        setShowCurrentPassword(false);
                                        setShowPassword(false);
                                        setShowRepeatPassword(false);
                                        setIsCurrentPasswordValid(null);
                                        setError({type: '', message: ''});
                                    }} 
                                    className={'close-change-password-btn'}
                                    type="button"
                                >
                                    Отменить
                                </button>
                            )}
                            <button 
                                onClick={handleSubmitForm} 
                                className={`order-detail-btn order-detail-btn-submit setup-customer__button ${isSaveDisabled ? 'button_disabled' : ''}`}
                                type="button"
                                disabled={isSaveDisabled}
                            >
                                Сохранить
                            </button>
                        </div>
                    </>
                ) : null}
            </div>
            {!resetCode && userId ? (
                <div className="setup-customer__section setup-currency">
                    <h3 className="setup-customer__title">Валюта</h3>
                    <div className="setup-currency__row">
                        <label className="setup-currency__label" htmlFor="price-currency">Валюта:</label>
                        <div className="setup-currency__select-wrapper">
                            <select
                                id="price-currency"
                                className="setup-currency__select"
                                value={priceCurrency}
                                onChange={handleCurrencyChange}
                                disabled={currencySaving}
                            >
                                <option value="RUB">Рубли</option>
                                <option value="USD">Доллары</option>
                            </select>
                        </div>
                    </div>
                    {currencyMessage ? (
                        <p className={`alert ${currencyMessage === 'Валюта сохранена' ? 'alert-success' : ''}`}>{currencyMessage}</p>
                    ) : null}
                </div>
            ) : null}
        </>
    );
};

export default TabProfile;